import { useCallback, useEffect, useRef, useState } from "react";
import { fetchHotWalletDeposits, type ApiErrorInfo, type ApiHotWalletDeposit } from "@/lib/api";

interface UseHotWalletDepositsResult {
  deposits: ApiHotWalletDeposit[];
  loading: boolean;
  error: ApiErrorInfo | null;
  refresh: () => void;
}

/** List hook for HW-S2 (addresses & deposits explorer). */
export function useHotWalletDeposits(walletId: string | undefined): UseHotWalletDepositsResult {
  const [deposits, setDeposits] = useState<ApiHotWalletDeposit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiErrorInfo | null>(null);
  // Only the most recent load may write state. Switching wallet (or refreshing)
  // while an earlier request is still in flight otherwise lets the slower,
  // older response land last and put one wallet's deposits on another wallet's
  // page — an out-of-order write, not a network error, so nothing else catches
  // it. The id is bumped before the request, so a response for a superseded
  // wallet is discarded rather than rendered.
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    if (!walletId) {
      setDeposits([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetchHotWalletDeposits(walletId);
      if (seq !== loadSeq.current) return;
      if (result.error) {
        setDeposits([]);
        setError(result.error);
      } else {
        setDeposits(result.items);
      }
    } catch {
      if (seq !== loadSeq.current) return;
      setDeposits([]);
      setError({ reasonCode: "NETWORK_ERROR" });
    } finally {
      // A superseded load must not clear the spinner the current one owns.
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [walletId]);

  useEffect(() => {
    load();
  }, [load]);

  return { deposits, loading, error, refresh: load };
}
